import { Pressable, Text, View, useWindowDimensions, type StyleProp, type ViewStyle } from 'react-native';

import { Card } from './Card';
import { colors, radius, typography } from '@/theme/tokens';

interface Props {
  label: string;
  value: string;
  hint?: string;
  trend?: { direction: 'up' | 'down' | 'flat'; label: string };
  icon?: React.ReactNode;
  accent?: 'magenta' | 'success' | 'warning' | 'danger' | 'neutral';
  style?: StyleProp<ViewStyle>;
  onPress?: () => void;
  active?: boolean;
}

const ACCENTS = {
  magenta: colors.magenta[700],
  success: colors.success[600],
  warning: colors.warning[600],
  danger: colors.danger[600],
  neutral: colors.ink[700],
} as const;

export function StatTile({ label, value, hint, trend, icon, accent = 'magenta', style, onPress, active }: Props) {
  const fg = ACCENTS[accent];
  const { width } = useWindowDimensions();
  // En pantallas angostas (celu) los achicamos para que entren 3-4 en una fila
  // en vez de apilarse uno debajo del otro.
  const narrow = width < 680;
  const minW = narrow ? 96 : 200;
  const pad = narrow ? 12 : 20;
  const minH = narrow ? 86 : 140;

  const content = (
    <>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <View style={{ gap: narrow ? 4 : 8, flex: 1 }}>
          <Text
            style={{
              fontSize: narrow ? 10 : 11,
              fontWeight: typography.weights.bold,
              color: active ? colors.magenta[700] : colors.ink[500],
              letterSpacing: narrow ? 0.4 : 1.2,
              textTransform: 'uppercase',
            }}
            numberOfLines={2}
          >
            {label}
          </Text>
          <Text
            style={{
              fontFamily: typography.display.fontFamily,
              fontSize: narrow ? 22 : 28,
              lineHeight: narrow ? 26 : 32,
              fontWeight: typography.weights.bold,
              color: colors.ink[900],
              letterSpacing: -0.3,
            }}
            numberOfLines={1}
          >
            {value}
          </Text>
          {hint ? <Text style={{ fontSize: narrow ? 11 : 12, color: colors.ink[500] }} numberOfLines={2}>{hint}</Text> : null}
        </View>
        {icon && !narrow ? <View style={{ marginLeft: 8 }}>{icon}</View> : null}
      </View>
      {trend ? (
        <View style={{ marginTop: 10 }}>
          <Text
            style={{
              fontSize: 12,
              fontWeight: typography.weights.semibold,
              color:
                trend.direction === 'up'
                  ? colors.success[600]
                  : trend.direction === 'down'
                    ? colors.danger[600]
                    : colors.ink[500],
            }}
          >
            {trend.direction === 'up' ? '▲' : trend.direction === 'down' ? '▼' : '■'} {trend.label}
          </Text>
        </View>
      ) : null}
    </>
  );

  const cardStyle: StyleProp<ViewStyle> = [
    { flex: 1, minWidth: minW, minHeight: minH },
    active
      ? {
          borderWidth: 2,
          borderColor: colors.magenta[500],
          backgroundColor: colors.magenta[50],
        }
      : null,
    style,
  ];

  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        style={({ pressed }) => [
          {
            flex: 1,
            minWidth: minW,
            transform: [{ scale: pressed ? 0.99 : 1 }],
            // @ts-ignore web only
            cursor: 'pointer',
            // @ts-ignore web only
            transitionProperty: 'transform',
            // @ts-ignore web only
            transitionDuration: '120ms',
          },
        ]}
      >
        <Card
          padding={pad}
          style={[
            { width: '100%', minHeight: minH },
            active
              ? { borderWidth: 2, borderColor: colors.magenta[500], backgroundColor: colors.magenta[50] }
              : null,
            style,
          ]}
        >
          {content}
        </Card>
      </Pressable>
    );
  }

  return <Card padding={pad} style={cardStyle}>{content}</Card>;
}
